"use client"

import type { LucideIcon } from "lucide-react"
import { TrendingUp, TrendingDown, Minus, Info } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { shareTrendLabel, type DeltaResult } from "@/lib/analytics/deltas"
import type { ReactNode } from "react"

export interface KpiCardProps {
  label: string
  icon: LucideIcon
  formattedValue: string | ReactNode
  delta?: DeltaResult | null
  /** After the change, what it is measured from; with no change, why there is none. */
  baseline?: string
  sparkline?: ReactNode
  subtitle?: ReactNode
  /** Under the change: what to do about the number — a link to the list behind it. */
  footer?: ReactNode
  tooltip?: string
  className?: string
}

const DIRECTION_ICON = {
  up: TrendingUp,
  down: TrendingDown,
  flat: Minus,
} as const

const COLOR_CLASSES = {
  green: "text-green-600",
  red: "text-red-600",
  gray: "text-muted-foreground",
} as const

export function DeltaBadge({ delta }: { delta: DeltaResult }) {
  const Icon = DIRECTION_ICON[delta.direction]
  const colorClass = COLOR_CLASSES[delta.color]

  return (
    <div className={cn("flex items-center gap-1 text-xs font-medium", colorClass)}>
      <Icon className="h-3 w-3" />
      <span>{deltaLabel(delta)}</span>
    </div>
  )
}

function deltaLabel(delta: DeltaResult): string {
  switch (delta.unit) {
    case "percent":
      if (delta.percent === null) return delta.direction === "up" ? "new" : "—"
      return `${delta.percent > 0 ? "+" : ""}${delta.percent.toFixed(1)}%`
    case "from":
      return shareTrendLabel(delta)
    default: {
      const exhaustive: never = delta
      return exhaustive
    }
  }
}

export function KpiCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-4" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-28" />
        <Skeleton className="mt-2 h-[40px] w-full" />
      </CardContent>
    </Card>
  )
}

export function KpiCard({ label, icon: Icon, formattedValue, delta, baseline, sparkline, subtitle, footer, tooltip, className }: KpiCardProps) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          <span>{label}</span>
          {tooltip && (
            <sup>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[250px]">
                    <p className="text-xs">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </sup>
          )}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formattedValue}</div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        {(delta || baseline) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            {delta && <DeltaBadge delta={delta} />}
            {baseline && <span className="text-xs text-muted-foreground">{baseline}</span>}
          </div>
        )}
        {footer && <div className="mt-2">{footer}</div>}
        {sparkline && <div className="mt-2">{sparkline}</div>}
      </CardContent>
    </Card>
  )
}
